import { AlertTriangle, Shield, Zap } from 'lucide-react'
import { motion, Variants } from 'framer-motion'

export const ProblemsSection = () => {
  // Container animation - subtle fade up
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  }

  // Heading animation - gentle fade and slide
  const headingVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  }

  // Card animation - subtle scale and fade
  const cardVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  }

  // Icon animation - gentle bounce
  const iconVariants: Variants = {
    hidden: { scale: 0 },
    visible: {
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 15,
        delay: 0.2
      }
    }
  }

  // Subtle hover effect for cards
  const cardHoverVariants: Variants = {
    hover: {
      y: -5,
      transition: {
        duration: 0.3,
        ease: 'easeOut'
      }
    }
  }

  return (
    <section className="bg-shire-light py-20 md:py-40">
      <motion.div
        className="mx-auto max-w-5xl px-6 text-center"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={containerVariants}
      >
        <motion.h2
          className="font-heading text-shire-dark mb-12 text-4xl md:text-5xl"
          variants={headingVariants}
        >
          You're Losing More Than Time
        </motion.h2>

        <motion.div
          className="grid gap-8 md:grid-cols-3"
          variants={containerVariants}
        >
          <motion.div
            className="border-shire-bark bg-shire-light rounded-xl border-2 border-dashed p-8 shadow-lg"
            whileHover="hover"
            variants={{ ...cardVariants, ...cardHoverVariants }}
          >
            <motion.div
              className="bg-shire-rose mx-auto mb-4 flex size-12 items-center justify-center rounded-full"
              variants={iconVariants}
            >
              <AlertTriangle className="text-shire-dark size-6" />
            </motion.div>
            <h3 className="font-heading text-shire-dark mb-4 text-xl">
              Missing Life's Moments
            </h3>
            <p className="text-shire-bark">
              While you scroll, conversations with loved ones slip by. Your
              career stagnates. Real experiences are replaced by watching other
              people live theirs.
            </p>
          </motion.div>

          <motion.div
            className="border-shire-bark bg-shire-light rounded-xl border-2 border-dashed p-8 shadow-lg"
            whileHover="hover"
            variants={{ ...cardVariants, ...cardHoverVariants }}
          >
            <motion.div
              className="bg-shire-rose mx-auto mb-4 flex size-12 items-center justify-center rounded-full"
              variants={iconVariants}
            >
              <Zap className="text-shire-dark size-6" />
            </motion.div>
            <h3 className="font-heading text-shire-dark mb-4 text-xl">
              Can't Just Delete
            </h3>
            <p className="text-shire-bark">
              Your accounts are tied to work, family, and friendships. Complete
              deletion isn't realistic. You need controlled access, not total
              elimination.
            </p>
          </motion.div>

          <motion.div
            className="border-shire-bark bg-shire-light rounded-xl border-2 border-dashed p-8 shadow-lg"
            whileHover="hover"
            variants={{ ...cardVariants, ...cardHoverVariants }}
          >
            <motion.div
              className="bg-shire-rose mx-auto mb-4 flex size-12 items-center justify-center rounded-full"
              variants={iconVariants}
            >
              <Shield className="text-shire-dark size-6" />
            </motion.div>
            <h3 className="font-heading text-shire-dark mb-4 text-xl">
              All Blocks Fail
            </h3>
            <p className="text-shire-bark">
              App blockers are disabled in seconds. Browser extensions get
              bypassed. Your willpower always loses to algorithms designed to
              capture attention.
            </p>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  )
}
